import { Button, Dropdown } from 'ccs-ui-components';
import React, { ChangeEvent, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';

import { useTranslation } from 'react-i18next';
import classes from './JoltMappingConfiguration.module.scss';
import { IInOutputjsnObj, IInputJson, IInputOutput, IJoltJson, IRowData, ISpec } from './TypeDefs';

const RenderError = ({ errorMsg }: { errorMsg: string }): React.ReactElement => (
  <div className={classes.joltConfigError}>{errorMsg}</div>
);
export const JoltConfigurations = () => {
  const { t: translate } = useTranslation();

  const [rowData, setRowsData] = useState<IRowData[]>([]);
  const [joltJSON, setJoltJSON] = useState<IJoltJson[]>([]);
  const [isShowTransform, setShowTransform] = useState(false);
  const [inputJSON, setInputJson] = useState<IInputJson | string>();
  const [outputJSON, setOutputJson] = useState<IInputJson | string>();
  const [inputJsnError, setInputJsnError] = useState<string>('');
  const [outputJsnError, setOutputJsnError] = useState<string>('');

  const inputJsonRef = useRef<HTMLTextAreaElement>(null);
  const outputJsonRef = useRef<HTMLTextAreaElement>(null);

  /**
   * @method joltSpecTransform
   * @description Transforms the input and output JSON based on the specified Jolt configuration.
   * If both inputJSON and outputJSON are arrays, it sets the Jolt configuration to shift each element of the array.
   * If both inputJSON and outputJSON are objects, it sets the Jolt configuration to transform the JSON based on the specified spec.
   */
  const joltSpecTransform = () => {
    if (inputJSON && outputJSON) {
      if (Array.isArray(inputJSON) && Array.isArray(outputJSON)) { // When Both Input and Output Json are array
        const joltJSONConfig = [{
          operation: 'shift',
          spec: {
            '*': '[&]'
          }
        }];
        setJoltJSON([...joltJSONConfig]);
      } else { // When Both Input and Output Json are Object
        setShowTransform(true);
        addConfigureRow();
        const joltJSONConfig = [{
          operation: 'shift',
          spec: {
          }
        }];
        setJoltJSON([...joltJSONConfig]);
      }
    }
  };

  /**
   * @method resetJoltSpec
   * @description Resets the Jolt specification based on the input and output JSON.
   * If both input and output JSON are arrays, the Jolt specification will be set to shift each element to its corresponding index.
   * If both input and output JSON are objects, the Jolt specification will be empty.
   */
  const resetJoltSpec = () => {
    let joltJSONConfig = null;
    if (Array.isArray(inputJSON) && Array.isArray(outputJSON)) { // When Both Input and Output Json are array
      joltJSONConfig = [{
        operation: 'shift',
        spec: {
          '*': '[&]'
        }
      }];
    } else { // When Both Input and Output Json are Object
      joltJSONConfig = [{
        operation: 'shift',
        spec: {
        }
      }];
    }

    return joltJSONConfig;
  };

  /**
   * @method clearTransForm
   * @description Clears the transformation by resetting the state variables and clearing the input/output JSON values.
   */
  const clearTransForm = () => {
    setRowsData([]);
    setJoltJSON([]);
    setShowTransform(false);
    setOutputJson('');
    setInputJson('');
    if (inputJsonRef.current) {
      inputJsonRef.current.value = '';
    }
    if (outputJsonRef.current) {
      outputJsonRef.current.value = '';
    }
    setInputJsnError('');
    setOutputJsnError('');
  };

  /**
   * @method onChangeInputJson
   * @description Handles the change event for the input JSON.
   * Parses the input JSON value and updates the state accordingly.
   * If the input JSON is invalid, sets an error message.
   */
  const onChangeInputJson = () => {
    try {
      setInputJsnError('');
      if (inputJsonRef.current && inputJsonRef.current.value) {
        const jsonValue = inputJsonRef.current.value;
        const jsonParse = JSON.parse(jsonValue);
        setInputJson(jsonParse);
        inputJsonRef.current.value = JSON.stringify(jsonParse, null, 4);
      }
    } catch (e) {
      setInputJsnError(translate('jolt_configuration.invalid_input') ?? '');
    }
  };

  /**
   * @method onChangeOutputJson
   * @description Handles the change event for the output JSON.
   * Parses the JSON value, sets the output JSON state, and updates the value in the input field.
   * If an error occurs during parsing, sets the output JSON error state.
   */
  const onChangeOutputJson = () => {
    try {
      setOutputJsnError('');
      if (outputJsonRef.current && outputJsonRef.current.value) {
        const jsonValue = outputJsonRef.current.value;
        const jsonParse = JSON.parse(jsonValue);
        setOutputJson(jsonParse);
        outputJsonRef.current.value = JSON.stringify(jsonParse, null, 4);
      }
    } catch (e) {
      setOutputJsnError(translate('jolt_configuration.invalid_output') ?? '');
    }
  };

  /**
   * @method addNewRow
   * @description Adds a new row to the JoltConfigurations table.
   * @param {number} rowIndex - The index of the row to add.
   */
  const addNewRow = ({ rowIndex }: { rowIndex: number }) => {
    if (rowData[rowIndex].inputError || rowData[rowIndex].outputError || !rowData[rowIndex].inputHierarchyText || !rowData[rowIndex].outputHierarchyText) {
      return;
    }
    addConfigureRow();
  };

  /**
   * @method addConfigureRow
   * @description Adds a new configuration row to the table.
   * This function checks if both `inputJSON` and `outputJSON` are defined. If either of them is undefined, the function returns without adding a new row.
   */
  const addConfigureRow = () => {
    if (!inputJSON || !outputJSON) return;
    const input = renderFields(inputJSON as IInOutputjsnObj);
    const output = renderFields(outputJSON as IInOutputjsnObj);
    const row: IRowData = {
      input,
      output,
      inputHierarchyText: '',
      outputHierarchyText: '',
      inputError: '',
      outputError: '',
      isJoltSet: false
    };
    setRowsData([...rowData, row]);
  };

  /**
   * @method removeRow
   * @description Removes a row from the rowData array based on the rowIndex.
   * @param {number} rowIndex - The index of the row to be removed.
   */
  const removeRow = ({ rowIndex }: { rowIndex: number }) => {
    const newRows = rowData.filter((row, idx) => idx !== rowIndex);
    setRowsData([...newRows]);
  };

  /**
   * @method getInputJsonKey
   * @description Retrieves the keys from a JSON object and returns them as an array of objects.
   * The returned array includes a default option with a value of '0'.
   * @param jsonObj - The JSON object from which to retrieve the keys.
   */
  const getInputJsonKey = (jsonObj: object) => {
    if (typeof jsonObj === 'object') {
      const keys = [{ name: 'Select', value: '0' }];
      Object.keys(jsonObj).forEach((value: string) => {
        keys.push({ value, name: value });
      });
      return keys;
    }
    return null;
  };

  /**
   * @method renderFields
   * @description Renders the fields for a given JSON field value.
   * @param jsonFieldValue - The JSON field value to render fields for.
   */
  const renderFields = (jsonFieldValue: IInOutputjsnObj): IInputOutput => {
    const keys = getInputJsonKey(jsonFieldValue);
    const rows: IInputOutput = {
      elems: keys ?? [],
      selectedValue: '',
      child: null
    };
    return rows;
  };

  /**
   * @method updateJoltMapping
   * @description Updates the Jolt mapping based on the rowData.
   * It iterates through each row and updates the joltSpec object accordingly.
   * Finally, it updates the rowData and joltJSON state variables.
   */
  const updateJoltMapping = () => {
    const joltJSON: IJoltJson[] = resetJoltSpec();
    rowData.forEach((row) => {
      const { inputHierarchyText, outputHierarchyText } = row;
      const arrInputHierText = inputHierarchyText.split('.');
      let joltSpec = joltJSON[0].spec;
      arrInputHierText.forEach((key, idx) => {
        if (idx === arrInputHierText.length - 1) {
          if (joltSpec[key]) {
            joltSpec[key] = (Array.isArray(joltSpec[key])) ? [outputHierarchyText, ...joltSpec[key] as string[]] : [outputHierarchyText, joltSpec[key]];
          } else {
            joltSpec[key] = outputHierarchyText;
          }
        } else if (!joltSpec[key]) {
          joltSpec[key] = {};
        }
        joltSpec = joltSpec[key] as ISpec;
      });
      row.isJoltSet = true;
      setJoltJSON(joltJSON);
    });
    setRowsData([...rowData]);
  };

  /**
   * @method clearJoltMapping
   * @description Clears the Jolt mapping by calling the joltSpecTransform function.
   */
  const clearJoltMapping = () => {
    joltSpecTransform();
  };

  /**
   * @method checkJoltInputKeyExists
   * @description Checks if a Jolt input key exists in the rowData array.
   * @param mode - The mode of the check ('input' or 'other').
   * @param inputHierarchyText - The input hierarchy text to check.
   * @param rowIndex - The index of the row to exclude from the check.
   */
  const checkJoltInputKeyExists = ({ mode, inputHierarchyText, rowIndex }: { mode: string, inputHierarchyText: string, rowIndex: number }) => {
    if (mode === 'input') {
      const filterData = rowData.filter((row, idx) => row.inputHierarchyText === inputHierarchyText && idx !== rowIndex);
      if (filterData && filterData.length) {
        return true;
      }
    } else {
      const filterData = rowData.filter((row, idx) => row.inputHierarchyText.includes(`${inputHierarchyText}.`) && idx !== rowIndex);
      if (filterData && filterData.length) {
        return true;
      }
    }

    return false;
  };

  /**
   * @method loadChild
   * @description Loads the child elements based on the selected value and updates the row data.
   * @param selectedValue - The selected value.
   * @param selectedObj - The selected object.
   * @param childElems - The child elements.
   * @param rowIndex - The index of the row.
   * @param mode - The mode ('input' or 'output').
   * @param selectedHierarchyText - The selected hierarchy text.
   */
  const loadChild = ({ selectedValue, selectedObj, childElems, rowIndex, mode, selectedHierarchyText }: { selectedValue: string, selectedObj: IInputOutput, childElems: object, rowIndex: number, mode: string, selectedHierarchyText: string }) => {
    rowData[rowIndex].inputError = '';
    rowData[rowIndex].outputError = '';
    if (mode === 'input' && selectedHierarchyText && checkJoltInputKeyExists({ inputHierarchyText: selectedHierarchyText, rowIndex, mode: 'input' })) {
      rowData[rowIndex].inputError = translate('jolt_configuration.config_wrong');
      setRowsData([...rowData]);
      return false;
    }

    if (mode === 'output' && checkJoltInputKeyExists({ inputHierarchyText: rowData[rowIndex].inputHierarchyText, rowIndex, mode: 'output' })) {
      rowData[rowIndex].outputError = translate('jolt_configuration.config_wrong');
      setRowsData([...rowData]);
      return false;
    }

    const finalHierarchyText = selectedHierarchyText ? `${selectedHierarchyText}.${selectedValue}` : selectedValue;
    const keys = getInputJsonKey(childElems);
    selectedObj.selectedValue = selectedValue;
    if (keys) {
      selectedObj.child = {
        elems: keys,
        selectedValue: '',
        child: null
      };
    } else {
      selectedObj.child = null;
    }
    if (mode === 'input') {
      rowData[rowIndex].inputHierarchyText = finalHierarchyText;
    } else {
      rowData[rowIndex].outputHierarchyText = finalHierarchyText;
    }
    setRowsData([...rowData]);
    return true;
  };

  /**
   * @method childComponent
   * @description Renders the child component based on the selected object.
   * @param selectedObj - The selected object.
   * @param jsonObj - The JSON object.
   * @param rowIndex - The index of the row.
   * @param mode - The mode (input or output).
   * @param selectedHierarchyText - The selected hierarchy text.
   * @returns The rendered child component.
   */
  const childComponent = ({ selectedObj, jsonObj, rowIndex, mode, selectedHierarchyText }: { selectedObj: IInputOutput, jsonObj: IInOutputjsnObj, rowIndex: number, mode: string, selectedHierarchyText: string }): React.ReactNode => {
    if (selectedObj) {
      return (
        <>
          <div className={classes.selectElemBlock}>
            <Dropdown
              data-testid={`${mode}Dropdown`}
              label=""
              data={selectedObj.elems}
              value={selectedObj.selectedValue}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => loadChild({ selectedValue: e.currentTarget.value, rowIndex, selectedObj, childElems: jsonObj[e.currentTarget.value], mode, selectedHierarchyText })}
            />
          </div>

          {selectedObj?.child && selectedObj?.child.elems && mode === 'input' && childComponent({ selectedObj: selectedObj.child, jsonObj: jsonObj[selectedObj.selectedValue], rowIndex, mode, selectedHierarchyText: selectedHierarchyText ? `${selectedHierarchyText}.${selectedObj.selectedValue}` : selectedObj.selectedValue })}

          {selectedObj?.child && selectedObj?.child.elems && mode === 'output' && childComponent({ selectedObj: selectedObj.child, jsonObj: jsonObj[selectedObj.selectedValue], rowIndex, mode, selectedHierarchyText: selectedHierarchyText ? `${selectedHierarchyText}.${selectedObj.selectedValue}` : selectedObj.selectedValue })}
        </>
      );
    }
    return '';
  };

  /**
   * @method gridRows
   * @description Renders the grid rows for JoltConfigurations.
   * @param rowData - The array of row data.
   * @returns The rendered grid rows.
   */
  const gridRows = ({ rowData }: { rowData: IRowData[] }): React.ReactNode => (
    <Container className={classes['config-wrapper']}>
      <h3>{translate('jolt_configuration.config_fields')}</h3>
      <Row>
        <Col md={5}><h5>{translate('jolt_configuration.input_json')}</h5></Col>
        <Col md={5}><h5>{translate('jolt_configuration.output_json')}</h5></Col>
        <Col md={2}><h5>{translate('jolt_configuration.txt_action')}</h5></Col>
      </Row>
      {
        rowData && rowData.length > 0 && rowData.map((item, rowIndex) => (
          <>
            <Row className={classes.configRows}>
              <Col md={5} className={classes.inputSection}>
                {childComponent({ selectedObj: item.input, jsonObj: inputJSON as IInOutputjsnObj, rowIndex, mode: 'input', selectedHierarchyText: '' })}
              </Col>
              <Col md={5} className={classes.outputSection}>
                {childComponent({ selectedObj: item.output, jsonObj: outputJSON as IInOutputjsnObj, rowIndex, mode: 'output', selectedHierarchyText: '' })}
              </Col>
              <Col md={2} className={classes.actionBlock}>
                {rowData.length > 1 ? <Button data-testid="deleteBtn" className={classes.actionBtn} onClick={() => removeRow({ rowIndex })} title="Delete Spec"><i className="bi bi-trash-fill" /><span>{translate('jolt_configuration.txt_delete')}</span> </Button> : <Button title="Delete Spec" className={`${classes.diabledAction}  ${classes.actionBtn}`}><i className="bi bi-trash-fill" /><span>{translate('jolt_configuration.txt_delete')}</span> </Button>}
                {((rowData.length - 1) === rowIndex) ? <Button data-testid="addBtn" className={classes.actionBtn} onClick={() => addNewRow({ rowIndex })} title="Add Row"><i className="bi bi-plus-circle-fill" /><span>{translate('jolt_configuration.txt_add_row')}</span> </Button> : <Button title="Add Row" className={`${classes.diabledAction} ${classes.actionBtn}`}><i className="bi bi-plus-circle-fill" /><span>{translate('jolt_configuration.txt_add_row')}</span> </Button>}
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                {item.inputError && <RenderError errorMsg={item.inputError} />}
              </Col>
              <Col md={5}>
                {item.outputError && <RenderError errorMsg={item.outputError} />}
              </Col>
            </Row>
          </>
        ))
      }
      <div className={`d-flex flex-row-reverse ${classes.configButtons}`}>
        <Button variant="primary" data-testid="updateConfigBtn" onClick={() => { updateJoltMapping(); }}>{translate('jolt_configuration.txt_update_config')}</Button>
        <Button variant="secondary" data-testid="clearConfigBtn" onClick={() => { clearJoltMapping(); }}>{translate('jolt_configuration.txt_clear_all')}</Button>
      </div>
    </Container>
  );

  return (
    <Container className={classes['jolt-wrapper']}>
      <Row className={classes['input-wrapper']}>
        <Col sm={4}>
          <div className={classes.textAreaHeader}>
            <Form.Label>{translate('jolt_configuration.input_json')}</Form.Label>
          </div>
          <textarea aria-label="inputJson" data-testid="inputJson" name="inputJson" className={classes.jsonTextArea} ref={inputJsonRef} onBlur={() => { onChangeInputJson(); }} />
          {inputJsnError && <RenderError errorMsg={inputJsnError} />}
        </Col>
        <Col sm={4}>
          <div className={classes.textAreaHeader}>
            <Form.Label>{translate('jolt_configuration.output_json')}</Form.Label>
          </div>
          <textarea aria-label="outputJson" data-testid="outputJson" name="outputJson" className={classes.jsonTextArea} ref={outputJsonRef} onBlur={() => { onChangeOutputJson(); }} />
          {outputJsnError && <RenderError errorMsg={outputJsnError} />}
        </Col>
        <Col sm={4}>
          <div className={classes.textAreaHeader}>
            <Form.Label>{translate('jolt_configuration.txt_jolt_spec')}</Form.Label>
            <div>
              <Button data-testid="getSpecBtn" variant="primary" className={classes['transform-button']} onClick={() => { joltSpecTransform(); }}>{translate('jolt_configuration.txt_get_spec')}</Button>
              <Button data-testid="clearBtn" variant="secondary" className={classes['clear-button']} onClick={() => { clearTransForm(); }}>{translate('jolt_configuration.txt_clear')}</Button>
            </div>
          </div>
          <textarea name="joltJson" aria-label="joltJson" data-testid="joltJson" className={classes.jsonTextArea} value={JSON.stringify(joltJSON, null, 4)} />
        </Col>
      </Row>
      {isShowTransform && gridRows({ rowData })}
    </Container>
  );
};
